import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { filter, map, merge, mergeMap, Observable, switchMap } from 'rxjs';
import { SmoMoonType } from '../../../../Models/Smo/SmoMoonType';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { radians } from '../../../../Utils/Trigonometry';
import { Nullable } from '../../../../Utils/Types';
import { ImageCropper } from '../../../ImageCropper/ImageCropper';
import { ImageFilterer } from '../../../ImageFilterer/ImageFilterer';
import { TextProcessingFrame } from '../../../TextProcessing/Models/TextProcessingFrame';
import { StoreDependencies } from '../StoreDependencies';
import { StoreState } from '../StoreState';
import DebugActions from './DebugActions';

function processSingleMoonImage(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(DebugActions.processSingleMoonImage.match),
    filter(() => isDebugMode($state)),
    filter(() => isProcessingImageRunning($state)),
    mapPayload(),
    map((image) => ({
      image,
      brightness: $state.value.videoAnalysis.filterBrightness[SmoMoonType.single].value ?? -0.45
    })),
    switchMap(({ image, brightness }) =>
      merge(
        cropImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.single].titleFrame(brightness),
          dependencies.imageCropper!
        ).pipe(map(urlFromImage), map(DebugActions.addSingleMoonCroppedTitle)),
        cropAndFilterImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.single].titleFrame(brightness),
          dependencies.imageCropper!,
          dependencies.imageFilterer!
        ).pipe(map(urlFromImage), map(DebugActions.addSingleMoonCroppedFilteredTitle)),
        cropImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.single].nameFrame(brightness),
          dependencies.imageCropper!
        ).pipe(map(urlFromImage), map(DebugActions.addSingleMoonCroppedName)),
        cropAndFilterImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.single].nameFrame(brightness),
          dependencies.imageCropper!,
          dependencies.imageFilterer!
        ).pipe(map(urlFromImage), map(DebugActions.addSingleMoonCroppedFilteredName))
      )
    )
  );
}

function processMultiMoonImage(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(DebugActions.processMultiMoonImage.match),
    filter(() => isDebugMode($state)),
    filter(() => isProcessingImageRunning($state)),
    mapPayload(),
    map((image) => ({
      image,
      brightness: $state.value.videoAnalysis.filterBrightness[SmoMoonType.multi].value ?? -0.45
    })),
    switchMap(({ image, brightness }) =>
      merge(
        cropImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.multi].nameFrame(brightness),
          dependencies.imageCropper!
        ).pipe(map(urlFromImage), map(DebugActions.addMultiMoonCroppedName)),
        cropAndFilterImage(
          image,
          dependencies.textProcessingManager!.processingData[SmoMoonType.multi].nameFrame(brightness),
          dependencies.imageCropper!,
          dependencies.imageFilterer!
        ).pipe(map(urlFromImage), map(DebugActions.addMultiMoonCroppedFilteredName))
      )
    )
  );
}

function cropImage(
  image: HTMLImageElement,
  textProcessingFrame: TextProcessingFrame,
  imageCropper: ImageCropper
): Observable<HTMLImageElement> {
  return imageCropper
    .rotateImage(image, radians(textProcessingFrame.rotation))
    .pipe(mergeMap((image) => imageCropper.cropImage(image, textProcessingFrame.crop)));
}

function cropAndFilterImage(
  image: HTMLImageElement,
  textProcessingFrame: TextProcessingFrame,
  imageCropper: ImageCropper,
  imageFilterer: ImageFilterer
): Observable<HTMLImageElement> {
  return cropImage(image, textProcessingFrame, imageCropper).pipe(
    switchMap((image) => imageFilterer.applyFilters(image, textProcessingFrame.filters))
  );
}

function urlFromImage(image: HTMLImageElement): string {
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;

  const ctx: Nullable<CanvasRenderingContext2D> = canvas.getContext('2d');
  ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/png');
}

function isDebugMode($state: StateObservable<StoreState>): boolean {
  return $state.value.debug.debugMode;
}

function isProcessingImageRunning($state: StateObservable<StoreState>): boolean {
  return !$state.value.debug.isImageProcessingPaused;
}

export default combineEpics(processSingleMoonImage, processMultiMoonImage);
